import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import Filter1OutlinedIcon from "@mui/icons-material/Filter1Outlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { parse, format } from "date-fns";
import moment from "moment";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { customGetAPI } from "services/utils/CustomAPI";
import sessionHandling from "services/utils/notificationUtils";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { CustomAutoComplete } from "components/CustomComponents";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  HEADER_ACTION_BUTTON_WIDTH,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  sharedListPageSurfaceSx,
  sharedStickyCustomFilterBarSx,
  sharedStickyFooterBarSx,
} from "components/DesignSystem/sharedUiPatterns";
import { Apply } from "./Apply";
import { ViewLeave } from "./ViewLeave";
import { LeaveBalanceCards } from "./LeaveBalanceCards";
import { LeaveStatusCounts } from "./LeaveStatusCounts";
import { LeaveHistoryTable } from "./LeaveHistoryTable";
import { LeaveSummaryPanel } from "./LeaveSummaryPanel";
import { cardSx, appliedOn } from "./leaveHelpers";

// History-table columns for the Leave screen. Typed so the shared CustomFilter
// (search / column filters / sort) and the SavedViewSelector "Default View"
// dropdown work exactly as they do on My Zone > My Attendance.
const leaveHistoryHeaders = [
  { name: "Leave Type", key: "leave_type", type: "autocomplete", icon: <CategoryOutlinedIcon fontSize="small" /> },
  { name: "From", key: "from_date", type: "date", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { name: "To", key: "to_date", type: "date", icon: <EventOutlinedIcon fontSize="small" /> },
  { name: "Days", key: "business_days", type: "number", icon: <Filter1OutlinedIcon fontSize="small" /> },
  { name: "Reason", key: "reason", type: "string", icon: <SubjectOutlinedIcon fontSize="small" /> },
  { name: "Status", key: "status", type: "autocomplete", icon: <CheckCircleOutlineIcon fontSize="small" /> },
  { name: "Applied On", key: "applied", type: "date", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
];

// Normalize the API status string to one of the four canonical buckets the
// summary tiles/filter use (case- and spelling-tolerant), leaving anything
// unexpected untouched.
const canonicalStatus = (status) => {
  const k = (status || "").trim().toLowerCase();
  if (k === "approved") return "Approved";
  if (k === "pending") return "Pending";
  if (k === "rejected") return "Rejected";
  if (k === "cancelled" || k === "canceled") return "Cancelled";
  return status;
};

// Redesigned My Zone > Apply > Leave screen (image 2). The three data sources
// are the same ones the app already relies on:
//   - leaves-years ....... available leave years (the date-range selector)
//   - leavesummary ....... this year's leave_requests -> history + tallies
//   - leavetypefilter .... per-type credited/used -> the balance cards
// The New/Apply drawer and the ViewLeave drawer are reused unchanged, so
// creating and viewing a leave still runs through their existing flows.
// Padding (md+) of the My Requests scroll container in MyZoneApply/index.js.
const SCROLL_PAD = 16;

export const Leave = ({ subModuleId }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails, token, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );

  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  useEffect(() => {
    if (!customFilterNode) return undefined;
    const update = () => setCustomFilterHeight(customFilterNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  useEffect(() => {
    if (!footerNode) return undefined;
    const update = () => setFooterHeight(footerNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);
  const [loading, setLoading] = useState(true);
  const [leaveYear, setLeaveYear] = useState([]);
  const [filteredValue, setFilteredValue] = useState("");
  const [selectedYearId, setSelectedYearId] = useState(null);
  // The request list defaults to This Month. The leave year below only drives
  // the balance cards (via leave_year_id); it no longer seeds this range.
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "monthEnd" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const [reCallApi, setReCallApi] = useState(true);
  const [leaveTypeRefreshKey, setLeaveTypeRefreshKey] = useState(0);

  const [leaveData, setLeaveData] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  const [statusFilter, setStatusFilter] = useState("");

  // CustomFilter (search / column filters / sort) output + pagination.
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [viewLeaveDetails, setViewLeaveDetails] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  const checkAccess = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Leaves",
  );

  const location = useLocation();

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = parse(dateString, "dd-MM-yyyy", new Date());
    return format(date, "yyyy-MM-dd");
  };

  // Auto-open Apply drawer when navigating with ?apply=true or ?showApply=true
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenApply = params.get("apply") === "true" || params.get("showApply") === "true";
    if (shouldOpenApply && checkAccess) {
      setIsCreatePanelOpen(true);
    }
  }, [location.search, checkAccess]);

  // 1) Leave years -> default the balance cards to the first (current) year.
  useEffect(() => {
    const fetchLeaveYears = async () => {
      try {
        const response = await fetch(`${domain}leaves-years/`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        const res = await response.json();
        if (response.ok) {
          const results = res.results || [];
          setLeaveYear(
            results.map((v) => ({
              id: v.id,
              label: `${v.name} (${v.start_date} - ${v.end_date})`,
              start_date: v.start_date,
              end_date: v.end_date,
              filteredValue: `${v.name} (${v.start_date} - ${v.end_date})`,
            })),
          );
          if (results[0]) {
            setFilteredValue(
              `${results[0].name} (${results[0].start_date} - ${results[0].end_date})`,
            );
            setSelectedYearId(results[0].id);
          }
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Leave] leave-years failed:", error);
        showToast("error", "Couldn't load your leaves. Please refresh the page.");
        setLoading(false);
      }
    };
    fetchLeaveYears();
  }, [domain, token]);

  // 2) Leave requests for the selected year.
  useEffect(() => {
    const fetchLeaveDetails = async () => {
      setLoading(true);
      const res = await customGetAPI(
        `${domain}leavesummary/?user_id=${userDetails?.id}&start_date=${startDate}&end_date=${endDate}&company_id=${requestCompanyId}`,
      );
      setLeaveData(res?.responseDetails?.leave_count?.leave_requests || []);
      setLeaveTypeRefreshKey((previous) => previous + 1);
      setLoading(false);
      setReCallApi(false);
    };
    if (startDate && endDate && reCallApi) fetchLeaveDetails();
  }, [domain, startDate, endDate, reCallApi, userDetails?.id, requestCompanyId]);

  // 3) Per-type balances (credited / used) for the balance cards + summary.
  useEffect(() => {
    const fetchLeaveTypes = async () => {
      try {
        const response = await fetch(
          `${domain}leavetypefilter/?user_id=${userDetails?.id}${
            selectedYearId ? `&leave_year_id=${selectedYearId}` : ""
          }`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setLeaveTypes(Array.isArray(res) ? res : []);
        } else if (response.status === 409) {
          sessionHandling();
        }
      } catch (error) {
        console.error("[Leave] leavetypefilter failed:", error);
      }
    };
    if (userDetails?.id) fetchLeaveTypes();
  }, [domain, token, userDetails?.id, selectedYearId, leaveTypeRefreshKey]);

  const onSelectYear = (event, value) => {
    if (value && value.filteredValue) {
      setFilteredValue(value.filteredValue);
      setSelectedYearId(value.id);
      setStartDate(formatDate(value.start_date));
      setEndDate(formatDate(value.end_date));
      setDateRangeLabel(value.label?.split(" (")?.[0] || "This Year");
      setStatusFilter("");
      setPage(0);
      setReCallApi(true);
    }
  };

  // Derived tallies — all from the two fetched arrays, memoized so filtering the
  // table doesn't recompute the balance/summary numbers.
  const statusCounts = useMemo(() => {
    return leaveData.reduce((acc, r) => {
      const s = canonicalStatus(r.status);
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
  }, [leaveData]);

  const pendingByType = useMemo(() => {
    return leaveData
      .filter((r) => canonicalStatus(r.status) === "Pending")
      .reduce((acc, r) => {
        acc[r.leave_type] =
          (acc[r.leave_type] || 0) + (Number(r.business_days) || 0);
        return acc;
      }, {});
  }, [leaveData]);

  // Rows fed to CustomFilter: pre-filtered by the clickable status tiles, then
  // normalized so every history column addresses a flat row[key] the shared
  // filter/search/sort can read directly.
  const filterInputRows = useMemo(() => {
    return leaveData
      .filter((r) => !statusFilter || canonicalStatus(r.status) === statusFilter)
      .map((r) => ({
        ...r,
        reason: r.applied_reason || r.description || "",
        applied: appliedOn(r),
      }));
  }, [leaveData, statusFilter]);

  const handleFilteredDataChange = useCallback(
    (filtered = []) => {
      setTableRows(filtered);
      setPage((prev) => (prev * rowsPerPage >= filtered.length ? 0 : prev));
    },
    [rowsPerPage],
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDateRangeChange = useCallback(
    (rangeStart, rangeEnd, rangeLabel) => {
      const nextStart = moment(rangeStart).format("YYYY-MM-DD");
      const nextEnd = moment(rangeEnd).format("YYYY-MM-DD");
      // The picker also reports its current value when it (re)mounts, e.g.
      // after picking a leave year moves the range. That is not a user pick,
      // so it must not clear the selected leave year or refetch.
      if (nextStart === startDate && nextEnd === endDate) return;

      setStartDate(nextStart);
      setEndDate(nextEnd);
      setDateRangeLabel(rangeLabel || "Custom Range");
      setSelectedYearId(null);
      setFilteredValue("");
      setStatusFilter("");
      setPage(0);
      setReCallApi(true);
    },
    [startDate, endDate],
  );

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "myzone_leave_saved_views",
    fullHeaders: leaveHistoryHeaders,
    screenAccess: true,
    onViewChange: () => setPage(0),
  });

  const reportMetadata = useMemo(
    () => ({
      "User Name":
        userDetails?.first_name ||
        userDetails?.user_name ||
        userDetails?.employee_name ||
        userDetails?.username ||
        "N/A",
      "Employee ID": userDetails?.employee_id || "N/A",
      "Leave Year": filteredValue || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [filteredValue, userDetails],
  );

  const paginatedRows = useMemo(
    () => tableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [tableRows, page, rowsPerPage],
  );

  const yearLabel = startDate ? startDate.slice(0, 4) : "";

  const openApply = () => {
    if (checkAccess) {
      setIsCreatePanelOpen(true);
    } else {
      showToast("error", "Access denied: You are not authorized to apply for leave");
    }
  };

  if (loading) return <Loader />;

  return (
    <React.Fragment>
      {/* The parent owns the viewport height, padding and breadcrumb. */}
      <Box sx={{ ...sharedListPageSurfaceSx, minHeight: 0 }}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={designType.pageTitle}>
              Leave
            </Typography>
            <Typography sx={designType.pageSubtitle}>
              Manage your leave requests, balances and history
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleDateRangeChange}
              includeCurrentDate
              showToday
            />
            {checkAccess ? (
              <PrimaryButton
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={{
                  minWidth: { xs: "100%", sm: HEADER_ACTION_BUTTON_WIDTH },
                  width: { xs: "100%", sm: "auto" },
                }}
                onClick={openApply}
              >
                New Request
              </PrimaryButton>
            ) : null}
          </Stack>
        </Stack>

        {/* Quick Links — horizontal bar */}
        <Box sx={{ mb: 2 }}>
          <LeaveSummaryPanel />
        </Box>

        <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
          {/* Leave-year selector row */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Typography sx={{ ...designType.helperText, color: designColors.slate600 }}>
              Leave Year:{" "}
              <Typography
                component="span"
                sx={{ fontSize: 13, color: designColors.tealDark, fontWeight: 700 }}
              >
                {filteredValue || "—"}
              </Typography>
            </Typography>
            <Box sx={{ minWidth: { xs: "100%", sm: 300 } }}>
              <CustomAutoComplete
                dataList={leaveYear}
                selectedValue={filteredValue}
                updateDataFunction={onSelectYear}
                placeHolder="Select leave year"
                fullWidth
              />
            </Box>
          </Stack>
        </Box>

        <LeaveBalanceCards
          leaveTypes={leaveTypes}
          pendingByType={pendingByType}
          yearLabel={yearLabel}
        />

        <LeaveStatusCounts
          counts={statusCounts}
          activeStatus={statusFilter}
          onSelect={(s) => {
            setStatusFilter(s);
            setPage(0);
          }}
        />

        {/* My Attendance-style toolbar: Default View · Search · Download · Sort · Filters */}
        {/* My Requests scrolls inside its own container (md+) that already starts
            below the app bar and has 16px padding; sticky offsets are measured
            from inside that padding, so pull them out by SCROLL_PAD or scrolled
            rows show through the gap above the bar / below the footer. */}
        <Box
          ref={customFilterRef}
          sx={{ ...sharedStickyCustomFilterBarSx, top: { md: `-${SCROLL_PAD}px` } }}
        >
          <CustomFilter
            title="Leave History"
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={filterInputRows}
            filteredData={tableRows}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={true}
            showSaveButton={true}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
            labeledToolbar={true}
            searchPlaceholder="Search by leave type, reason or status..."
          />
        </Box>

        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Box>
            <LeaveHistoryTable
              headers={visibleHeaders}
              stickyTop={{ xs: APP_BAR_OFFSET, md: customFilterHeight - SCROLL_PAD }}
              footerHeight={footerHeight}
              rows={paginatedRows}
              onRowClick={(row) => {
                setSelectedData(row);
                setViewLeaveDetails(true);
              }}
            />
          </Box>

          <Box
            ref={footerRef}
            sx={{ ...sharedStickyFooterBarSx, bottom: { xs: -1, md: `-${SCROLL_PAD + 1}px` } }}
          >
            {paginationFooter({
              page,
              rowsPerPage,
              total: tableRows.length,
              itemLabel: "records",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>

      {isCreatePanelOpen ? (
        <Apply
          open={isCreatePanelOpen}
          onClose={() => setIsCreatePanelOpen(false)}
          setReCallApi={setReCallApi}
          leaveYear={leaveYear}
          filteredValue={filteredValue}
          setFilteredValue={setFilteredValue}
        />
      ) : null}
      {viewLeaveDetails ? (
        <ViewLeave
          open={viewLeaveDetails}
          onClose={() => setViewLeaveDetails(false)}
          selectedData={selectedData}
          leaveYearId={selectedYearId}
          checkAccess={checkAccess}
          setReCallApi={() => {
            setReCallApi(true);
            setLeaveTypeRefreshKey((previous) => previous + 1);
          }}
        />
      ) : null}
    </React.Fragment>
  );
};
